import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';

type Cluster = {
  name: string;
  authProvider: string;
  oidcTokenProvider?: string;
  dashboardUrl?: string;
};

const useStyles = makeStyles(theme => ({
  mono: { fontFamily: 'monospace' },
  chipRow: { display: 'flex', alignItems: 'center', gap: 6 },
  hint: { marginTop: theme.spacing(2) },
}));

const NoClusters = () => (
  <InfoCard title="No Kubernetes clusters connected">
    <Typography variant="body1" gutterBottom>
      No clusters are configured yet. Connect the Rancher-managed cluster on
      Proxmox (<code>k8s-00</code>…<code>k8s-08</code>) by:
    </Typography>
    <ol>
      <li>
        Applying a read-only ServiceAccount + <code>view</code>{' '}
        ClusterRoleBinding + token Secret on the cluster.
      </li>
      <li>
        Setting <code>K8S_API_URL</code> and <code>K8S_SA_TOKEN</code> in{' '}
        <code>/etc/backstage.env</code>.
      </li>
      <li>
        Enabling the cluster entry under{' '}
        <code>kubernetes.clusterLocatorMethods</code> in{' '}
        <code>app-config.production.yaml</code> and restarting the backend.
      </li>
    </ol>
    <Typography variant="body2" color="textSecondary">
      Per-workload details (pods, deployments, services) appear on each
      component's <strong>Kubernetes</strong> tab once its objects are labelled{' '}
      <code>backstage.io/kubernetes-id</code>.
    </Typography>
  </InfoCard>
);

export const KubernetesPage = () => {
  const classes = useStyles();
  const api = useApi(kubernetesApiRef);
  const [clusters, setClusters] = useState<Cluster[]>();
  const [error, setError] = useState<Error>();

  const load = useCallback(async () => {
    try {
      setError(undefined);
      const base = await api.getClusters();
      // Enrich with dashboardUrl where available (best-effort, per cluster).
      const enriched = await Promise.all(
        base.map(async c => {
          try {
            const full = await api.getCluster(c.name);
            return { ...c, dashboardUrl: full?.dashboardUrl };
          } catch {
            return c;
          }
        }),
      );
      setClusters(enriched);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Page themeId="tool">
      <Header
        title="Kubernetes"
        subtitle="Clusters connected to Backstage (read-only)"
      />
      <Content>
        <ContentHeader title="Clusters">
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
          >
            Refresh
          </Button>
        </ContentHeader>

        {error && <ResponseErrorPanel error={error} />}
        {!error && !clusters && <Progress />}
        {!error && clusters && clusters.length === 0 && <NoClusters />}

        {!error && clusters && clusters.length > 0 && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cluster</TableCell>
                  <TableCell>Auth provider</TableCell>
                  <TableCell>OIDC token provider</TableCell>
                  <TableCell>Dashboard</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clusters.map(c => (
                  <TableRow key={c.name}>
                    <TableCell className={classes.mono}>{c.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.authProvider}
                        style={{ height: 20 }}
                      />
                    </TableCell>
                    <TableCell>{c.oidcTokenProvider ?? '—'}</TableCell>
                    <TableCell>
                      {c.dashboardUrl ? (
                        <Link
                          to={c.dashboardUrl}
                          target="_blank"
                          rel="noopener"
                        >
                          <Box className={classes.chipRow}>
                            open <OpenInNewIcon fontSize="inherit" />
                          </Box>
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.hint}
            >
              Per-workload details (pods, deployments, services) appear on each
              component's <strong>Kubernetes</strong> tab — annotate a component
              with <code>backstage.io/kubernetes-id</code> and label its
              workloads to match.
            </Typography>
          </>
        )}
      </Content>
    </Page>
  );
};
